import { DATE_ONLY_REGEX, type DateOnlyString } from "../schemas/dates";

/**
 * Normalizes a date input to a date-only `YYYY-MM-DD` string.
 *
 * Accepts either a `Date` or a string. Strings already prefixed with a
 * `YYYY-MM-DD` calendar day (including full ISO timestamps like
 * `2026-11-30T12:00:00Z`) are truncated to that day without any timezone
 * interpretation. Any other string is parsed as a `Date`. `Date` inputs are
 * read in UTC so a noon-anchored timestamp maps to its intended calendar day.
 *
 * @param value - A `Date` or date-like string.
 * @returns The calendar day as `YYYY-MM-DD`.
 */
export function toDateOnlyString(value: string | Date): DateOnlyString {
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return dateToUtcDateOnly(new Date(value));
  }
  return dateToUtcDateOnly(value);
}

/**
 * Returns today's calendar day in the local timezone as `YYYY-MM-DD`.
 */
export function todayDateOnlyString(): DateOnlyString {
  return toLocalDateOnlyString(new Date());
}

/**
 * Returns the calendar day of a `Date` in the local timezone as `YYYY-MM-DD`.
 */
export function toLocalDateOnlyString(date: Date): DateOnlyString {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Determines whether a date-only value is strictly before today. A value equal
 * to today is not expired — the calendar day is still valid through its end.
 *
 * @param value - A date-only `YYYY-MM-DD` string, or null/undefined.
 * @returns True when the value is a valid date strictly before today.
 */
export function isDateOnlyExpired(
  value: string | null | undefined,
): boolean {
  if (!value || !DATE_ONLY_REGEX.test(value)) {
    return false;
  }
  return value < todayDateOnlyString();
}

/**
 * Formats a date-only `YYYY-MM-DD` string for display without any timezone
 * shift. The value is rendered as its literal calendar day.
 *
 * @param value - A date-only string, or null/undefined.
 * @param options - Optional `Intl.DateTimeFormatOptions` for `toLocaleDateString`.
 * @param locale - Optional locale; defaults to the runtime locale.
 * @returns The formatted date, or null when the value is absent or invalid.
 */
export function formatDateOnly(
  value: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string | null {
  if (!value) {
    return null;
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString(locale, options);
}

/**
 * Formats a `Date` as its UTC calendar day in `YYYY-MM-DD` form.
 * @param date - The date to format.
 * @returns The UTC calendar day as `YYYY-MM-DD`.
 */
function dateToUtcDateOnly(date: Date): DateOnlyString {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
